import { AccountingMethodType, AccountingPeriodStatus } from './AccountingModels';
import { GeneralLedgerAccountType } from './GeneralLedgerAccountModel';

/** The BE's `JournalEntryType`, snake case. `opening` and `adjustment` are still accepted but no entry carries them. */
export type JournalEntryType =
  | 'opening'
  | 'billing'
  | 'collection'
  | 'credit_note_payout'
  | 'first_term_deferral'
  | 'setup_fee_deferral'
  | 'release'
  | 'ticket_reclass'
  | 'adjustment'
  | 'prepaid_deferral'
  | 'admission_release'
  | 'breakage'
  | 'sale'
  | 'show_release'
  | 'refund'
  | 'refund_fee'
  | 'payment'
  | 'third_party_claim'
  | 'gift_card_redeemed'
  | 'refund_payout'
  | 'gift_card_issued'
  | 'gift_card_opening'
  | 'discrepancy'
  | 'difference'
  | 'deposit'
  | 'deposit_release'
  | 'cutover'
  | 'transaction_difference'
  | 'settlement';

export type JournalEntryTypeGroup = 'operations' | 'subscriptions' | 'general';

/** The entry-type filter, in the BE's enum order per group. */
export const JOURNAL_ENTRY_TYPE_GROUPS: { key: JournalEntryTypeGroup; types: JournalEntryType[] }[] = [
  {
    key: 'operations',
    types: [
      'sale',
      'show_release',
      'refund',
      'refund_fee',
      'payment',
      'third_party_claim',
      'gift_card_redeemed',
      'refund_payout',
      'gift_card_issued',
      'gift_card_opening',
      'discrepancy',
      // PAUL-3854: the booked gap of a finished order or refund, on the Differences account.
      'transaction_difference',
      'deposit',
      'deposit_release',
      'settlement',
    ],
  },
  {
    key: 'subscriptions',
    types: [
      'billing',
      'collection',
      'credit_note_payout',
      'first_term_deferral',
      'setup_fee_deferral',
      'release',
      'ticket_reclass',
      'prepaid_deferral',
      'admission_release',
      'breakage',
    ],
  },
  { key: 'general', types: ['difference', 'cutover'] },
];

export const JOURNAL_ENTRY_TYPES: JournalEntryType[] = JOURNAL_ENTRY_TYPE_GROUPS.flatMap((group) => group.types);

/** `scheduled`: dated after today, listed but not counted in any total until its date. */
export type JournalEntryStatus = 'provisional' | 'closed' | 'exported' | 'scheduled';

/** The BE's `FinancialEventTransactionKind`, snake case: what an entry belongs to. */
export type JournalRootKind =
  | 'order'
  | 'refund'
  | 'subscription_invoice'
  | 'clearing'
  | 'gift_card_opening'
  | 'subscription_reservation'
  | 'subscription_instance'
  | 'method_cutover'
  | 'payout';

/** Set when every row of the entry (or the line) that has one shares it; a refund carries its order's id and number. */
export interface JournalRootLinks {
  order_id: string | null;
  order_number: number | null;
  refund_id: string | null;
  subscription_instance_id: string | null;
  /** The instance's earliest card's printed id, what the subscriptions list searches by; null without a card. */
  subscription_card_id: string | null;
}

/** Each refused order, refund or subscription once, as a positive amount; `roots` > 0 shows the banner. */
export interface JournalUnposted {
  amount: number;
  roots: number;
}

export type JournalLineType = 'plain' | 'base' | 'vat' | 'debtor';

/** A configuration problem in a 200 body. `{0}` kinds are sent literally; `message` is already localized. */
export interface JournalProblem {
  kind: string;
  field?: string | null;
  params: string[];
  message?: string | null;
}

export interface JournalPeriod {
  id: string;
  fiscal_year: number;
  period_number: number;
  label: string;
  start_date: string;
  end_date: string;
  status: AccountingPeriodStatus;
  closed_at: string | null;
  /** Null on a closed period = closed by the hourly task. */
  closed_by_user_id: string | null;
  exported_at: string | null;
  exported_by_user_id: string | null;
  auto_close_on: string | null;
  can_close: boolean;
  can_reopen: boolean;
  entry_count: number;
  first_entry_number: string | null;
  last_entry_number: string | null;
  /** Announced in the BE fix batch; absent until it lands. */
  reopened_at?: string | null;
}

export interface JournalPeriods {
  company_id: string;
  journal_code: string | null;
  journal_start: string | null;
  release_delay_days: number;
  periods: JournalPeriod[];
  problems: JournalProblem[];
}

export interface JournalRangePeriod {
  id: string;
  fiscal_year: number;
  period_number: number;
  label: string;
  start_date: string;
  end_date: string;
  status: AccountingPeriodStatus;
}

export interface JournalLine {
  account_id: string;
  account_code: string;
  account_name: string;
  /** Signed: + debit, - credit. */
  amount: number;
  line_type: JournalLineType;
  tax_id: string | null;
  vat_rate: number | null;
  vat_amount: number | null;
  product_id: string | null;
  product_name: string | null;
  fact_count: number;
  /** The sources call has something to show; a subscription line may have `fact_count` 0 and still have facts. */
  has_facts: boolean;
}

export interface JournalEntry extends JournalRootLinks {
  /** Always set; pass it as `entry_id` to the sources call. */
  id: string;
  entry_number: string | null;
  entry_date: string;
  period_id: string;
  period_label: string;
  entry_type: JournalEntryType;
  description: string;
  total: number;
  status: JournalEntryStatus;
  root_kind: JournalRootKind;
  /** Empty when loaded without lines (`include_lines=false`); `line_count` still counts them. */
  lines: JournalLine[];
  line_count: number;
}

/**
 * One group of the Journal tab, by the company's `export_grouping`; every entry lies in exactly one (start..end inclusive).
 * A closed group is a stored document and keeps its dates; an open period's groups follow the current setting.
 */
export interface JournalGroup {
  start_date: string;
  /** Inclusive. */
  end_date: string;
  period_id: string;
  period_label: string;
  /** Set once the period is closed. */
  entry_number: string | null;
  status: JournalGroupStatus;
  /** Its entries under the `entry_type` / `account_id` filters; 0 when the filters leave none. */
  entry_count: number;
  scheduled_count: number;
}

export type JournalGroupStatus = 'provisional' | 'closed' | 'exported';

export interface Journal {
  company_id: string;
  journal_code: string | null;
  journal_start: string | null;
  from_period_id: string;
  to_period_id: string;
  periods: JournalRangePeriod[];
  /** Empty with `include_entries=false`: the page loads them per group (`journal/entries`). */
  entries: JournalEntry[];
  /** Ordered by start, computed before the `entry_type` / `account_id` filters; each counts its entries under them. */
  groups: JournalGroup[];
  unposted: JournalUnposted;
  problems: JournalProblem[];
  /** The entries under the filters, also without `entries`. */
  entry_count: number;
  scheduled_count: number;
  has_provisional: boolean;
  /** The `account_id` filter's account, to name it without the lines; null without the filter. */
  account_code: string | null;
  account_name: string | null;
}

export interface JournalRangeQuery {
  from_period_id: string;
  to_period_id?: string | null;
}

/** One group of the range: both dates or neither. */
export interface JournalGroupDates {
  from_date?: string | null;
  to_date?: string | null;
}

export interface JournalQuery extends JournalRangeQuery {
  entry_type?: JournalEntryType | null;
  account_id?: string | null;
}

/** `include_entries: false`: the groups and counts only; the default `true` sends every entry with its lines. */
export interface JournalOverviewQuery extends JournalQuery {
  include_entries?: boolean | null;
}

/** One page of the entries, e.g. of one group: `take` defaults to 200 (max 1000), `skip` to 0. */
export interface JournalEntriesQuery extends JournalQuery, JournalGroupDates {
  skip?: number | null;
  take?: number | null;
  /** `false`: each entry with `lines: []` and its `line_count`. */
  include_lines?: boolean | null;
}

export interface JournalEntriesPage {
  entries: JournalEntry[];
  /** Every entry under the filters and dates, not only this page. */
  total: number;
  skip: number;
  take: number;
}

export interface JournalExportQuery extends JournalQuery, JournalGroupDates {}

/** PAUL-3815: a summary row sums one day, or one journal group (the company's `export_grouping`), per G/L account. */
export type JournalSummaryPer = 'day' | 'group';

export interface JournalSummaryQuery extends JournalQuery, JournalGroupDates {
  per: JournalSummaryPer;
}

export interface JournalSourcesQuery {
  period_id: string;
  entry_type: JournalEntryType;
  entry_date: string;
  account_id: string;
  /** The entry's `id`: the facts of that one entry. Without it, every entry of that type on that day and account. */
  entry_id?: string | null;
  /** Narrow the facts to one product / tax rate of the line. */
  product_id?: string | null;
  tax_id?: string | null;
}

export interface JournalSourceFact {
  id: string;
  type: string;
  source_table: string;
  source_id: string;
  transaction_id: string;
  version: number;
  subscription_instance_id: string | null;
  product_id: string | null;
  product_name: string | null;
  event_at: string;
  invoice_date: string | null;
  service_start: string | null;
  /** Exclusive, shown as sent (D2). */
  service_end: string | null;
  money_at: string | null;
  amount_incl_vat: number;
  amount_excl_vat: number;
  vat_amount: number;
  tax_rate: number | null;
  box_office_amount_excl_vat: number | null;
  payment_method_name: string | null;
  /** The order the fact belongs to; null on subscription invoices. */
  order_id?: string | null;
  order_number?: number | null;
}

/** The BE's sums over every fact the response returns (both facts calls are unpaged). */
export interface JournalFactTotals {
  amount_excl_vat: number;
  vat_amount: number;
}

export interface JournalSources {
  period_id: string;
  entry_type: JournalEntryType;
  entry_date: string;
  account_id: string;
  account_code: string;
  account_name: string;
  status: JournalEntryStatus;
  facts: JournalSourceFact[];
  totals: JournalFactTotals;
  problems: JournalProblem[];
}

export interface JournalBalanceTotals {
  opening: number;
  debit: number;
  credit: number;
  closing: number;
  /** Debit − credit over the range, as the BE sums it: the Movements view's Net column. */
  net: number;
}

/** Over every account, plus per account type. Both results are profit positive; the FE shows them, never derives them. */
export interface JournalBalancesTotals extends JournalBalanceTotals {
  balance_accounts: JournalBalanceTotals;
  result_accounts: JournalBalanceTotals;
  /** The range's own result: result accounts' credit − debit over the range. */
  period_result: number;
  /** Result to date: since the start of the fiscal year, or of the books if later (= −result_accounts.closing). */
  result: number;
}

export type JournalBalanceCheckKind =
  | 'debtors'
  | 'deferred'
  | 'prereceived'
  | 'gift_cards'
  | 'differences'
  | 'paynl_payouts';

/** "No G/L account": only a previous year's result carried into the range without an opening counter account. */
export const NO_LEDGER_ACCOUNT_ID = '00000000-0000-0000-0000-000000000000';

export interface JournalBalanceAccount extends JournalBalanceTotals {
  account_id: string;
  /** Null on the missing-account row. */
  account_code: string | null;
  account_name: string;
  account_type: GeneralLedgerAccountType;
  is_active: boolean;
  check_kind: JournalBalanceCheckKind | null;
  check_value: number | null;
  check_differs: boolean;
}

/** Balans (Balance and Differences accounts) or Winst- en verliesrekening (Result accounts); the keys of their totals. */
export type JournalBalancePart = 'balance_accounts' | 'result_accounts';

export interface JournalBalanceGroup extends JournalBalanceTotals {
  /** A group with accounts of both parts comes once per part; the BE lists Balans first. */
  part: JournalBalancePart;
  group_id: string | null;
  group_name: string | null;
  accounts: JournalBalanceAccount[];
}

export interface JournalBalances {
  company_id: string;
  from_period_id: string;
  to_period_id: string;
  from_date: string;
  to_date: string;
  accounting_method: AccountingMethodType | null;
  groups: JournalBalanceGroup[];
  totals: JournalBalancesTotals;
  unposted: JournalUnposted;
  problems: JournalProblem[];
}

/** One stored row on a Balances account, dated on or before today; debit and credit positive. */
export interface LedgerLine extends JournalRootLinks {
  date: string;
  kind: JournalEntryType;
  /** The row's entry: the Journal entry's `id`. */
  entry_id: string;
  period_id: string;
  status: JournalEntryStatus;
  debit: number;
  credit: number;
  has_facts: boolean;
}

export interface LedgerLines {
  company_id: string;
  account_id: string;
  from_period_id: string;
  to_period_id: string;
  from_date: string;
  to_date: string;
  debit: number;
  credit: number;
  lines: LedgerLine[];
  problems: JournalProblem[];
}

export interface LedgerLinesQuery extends JournalRangeQuery {
  account_id: string;
}

export interface LedgerFactsQuery {
  account_id: string;
  date: string;
  kind: JournalEntryType;
  /** The line's `entry_id`: the facts of that one row's entry. */
  entry_id?: string | null;
}

/** A fact corrected after it was booked is shown in its current version. */
export interface LedgerFacts {
  account_id: string;
  date: string;
  kind: JournalEntryType;
  facts: JournalSourceFact[];
  totals: JournalFactTotals;
}

export type DeferredRevenueFlag = 'equation' | 'negative' | 'check';

export interface DeferredRevenueTotals {
  opening: number;
  invoiced: number;
  adjustments: number;
  earned: number;
  breakage: number;
  closing: number;
  collected: number;
  open_debtors: number;
  check_value: number | null;
  /** Outside the sum: what the period's days up to today release. Only while the period runs; null otherwise. */
  release_to_date: number | null;
}

/** A roll-forward row with its warnings: a product, or a subscriber under it. */
export interface DeferredRevenueFlagged extends DeferredRevenueTotals {
  red_flag: boolean;
  flags: DeferredRevenueFlag[];
}

export interface DeferredRevenueRow extends DeferredRevenueFlagged {
  product_id: string | null;
  product_name: string | null;
}

export interface DeferredRevenue {
  company_id: string;
  period_id: string;
  period_label: string;
  start_date: string;
  end_date: string;
  status: AccountingPeriodStatus;
  products: DeferredRevenueRow[];
  totals: DeferredRevenueTotals;
  unposted: JournalUnposted;
  problems: JournalProblem[];
}

/** The product row's columns per subscription instance; the totals equal that product row. */
export interface DeferredRevenueContract extends DeferredRevenueFlagged {
  /** Null for the product's rows without an instance. */
  subscription_instance_id: string | null;
  /** The instance's first card, to open it in the subscriptions list; null without one. */
  subscription_card_id: string | null;
  user_first_name: string | null;
  user_last_name: string | null;
  /** Filled only for a punch card booked per admission; null otherwise. */
  tickets_used: number | null;
  ticket_cap: number | null;
}

export interface DeferredRevenueContracts {
  company_id: string;
  product_id: string;
  product_name: string;
  period_id: string;
  period_label: string;
  /** The shops the product is sold in, as the subscriptions list filters them. */
  shop_ids: string[];
  contracts: DeferredRevenueContract[];
  totals: DeferredRevenueTotals;
  problems: JournalProblem[];
}

export interface JournalExactExportBody extends JournalGroupDates {
  from_period_id: string;
  to_period_id?: string | null;
}

/** An xlsx response: the body and the name from `Content-Disposition` (null when the header has none). */
export interface DownloadedFile {
  blob: Blob;
  filename: string | null;
}
